import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, alpha } from "@mui/material";
import {
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { format, formatDistanceToNowStrict } from "date-fns";
import { tr } from "date-fns/locale";

import ActionButton from "../../atoms/ActionButton";
import SelectableListRow from "../../molecules/SelectableListRow";
import { RowLine, RowChip, RowEditorLine, RowMetaText } from "./_rowParts";
import ConfirmDialog from "../../common/ConfirmDialog";
import PhoneCallEditor from "./PhoneCallEditor";
import {
  LIST_ROW_SX,
  WORKSPACE_ROOT_SX,
  WorkspaceBody,
  WorkspaceSplit,
  WorkspaceSkeleton,
  WorkspaceEmpty,
  SelectPrompt,
  CreatingRow,
} from "./_workspaceParts";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";
import {
  readWorkspaceState,
  writeWorkspaceState,
} from "../../../pages/Examination/ExaminationSession/workspaceStorage";
import {
  useListPhoneCallsByPatientMutation,
  useDeletePhoneCallMutation,
} from "../../../store/secretaryApi";
import useTerms from "../../../hooks/useTerms";

const ROLE_LABEL = {
  Admin: "Yönetici",
  AdminStaff: "Admin",
  ClinicStaff: "Klinik",
};

const formatCallDate = (value) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "d MMM yyyy", { locale: tr });
  } catch (_) {
    return "—";
  }
};

const formatEditor = (user) => {
  if (!user) return null;
  const title = user.title ? `${user.title} ` : "";
  const full = `${title}${user.name || ""} ${user.surname || ""}`.trim();
  return {
    name: full || "—",
    role: ROLE_LABEL[user.role] || user.role || "",
  };
};

const formatRelativeTr = (value) => {
  if (!value) return "";
  try {
    return formatDistanceToNowStrict(new Date(value), {
      addSuffix: true,
      locale: tr,
    });
  } catch (_) {
    return "";
  }
};

/**
 * PhoneCallTab — düz liste | editör (Anamnez ile aynı dil). Kurallar:
 * docs/design-principles.md §8.3
 */
export default function PhoneCallTab({
  mode,
  patientId,
  token,
  canEdit = true,
  initialData,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
  onRecordChanged,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();

  // Local alert
  const [localAlert, setLocalAlert] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const alertTimerRef = useRef(null);
  const showLocal = useCallback((message, severity = "success", duration = 3500) => {
    if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    setLocalAlert({ open: true, severity, message });
    alertTimerRef.current = setTimeout(() => {
      setLocalAlert((a) => ({ ...a, open: false }));
      alertTimerRef.current = null;
    }, duration);
  }, []);
  const hideLocal = useCallback(() => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }
    setLocalAlert((a) => ({ ...a, open: false }));
  }, []);
  useEffect(
    () => () => {
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    },
    []
  );
  const showSuccess = useCallback((m) => showLocal(m, "success"), [showLocal]);
  const showError = useCallback((m) => showLocal(m, "error", 5000), [showLocal]);

  const [listPhoneCalls, { isLoading: isLoadingList }] =
    useListPhoneCallsByPatientMutation();
  const [deletePhoneCall] = useDeletePhoneCallMutation();

  const [list, setList] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const examinationId = initialData?.examinationId;
  const TAB_KEY = "phoneCalls";

  useEffect(() => {
    if (mode === "session") {
      setList(Array.isArray(initialData?.list) ? initialData.list : []);
      setInitialLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialData?.list]);

  const refresh = useCallback(async () => {
    if (mode !== "standalone" || !token || !patientId) return;
    try {
      const res = await listPhoneCalls({ token, patientId }).unwrap();
      setList(res?.data?.list || []);
    } catch (_) {
      showError("Telefon görüşmeleri yüklenemedi.");
    } finally {
      setInitialLoaded(true);
    }
  }, [mode, token, patientId, listPhoneCalls, showError]);

  useEffect(() => {
    if (mode === "standalone") refresh();
  }, [mode, refresh]);

  useEffect(() => {
    if (mode !== "session" || !examinationId) return;
    if (!initialLoaded || list.length === 0) return;
    const restored = readWorkspaceState(examinationId)?.openRecords?.[TAB_KEY];
    if (restored && list.some((r) => r._id === restored)) {
      setSelectedId(restored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, examinationId, initialLoaded]);

  useEffect(() => {
    if (mode !== "session" || !examinationId) return;
    writeWorkspaceState(examinationId, (prev) => ({
      ...prev,
      openRecords: { ...(prev.openRecords || {}), [TAB_KEY]: selectedId },
    }));
  }, [mode, examinationId, selectedId]);

  const selected = useMemo(
    () => list.find((r) => r._id === selectedId) || null,
    [list, selectedId]
  );

  const handleSelect = (id) => {
    setCreatingNew(false);
    setSelectedId(id);
  };

  const prevSelectedRef = useRef(null);
  const handleNew = () => {
    if (!canEdit) return;
    prevSelectedRef.current = selectedId;
    setSelectedId(null);
    setCreatingNew(true);
  };

  const handleCancelNew = () => {
    setCreatingNew(false);
    setSelectedId(prevSelectedRef.current);
  };

  const handleCreated = (created) => {
    setList((prev) => [created, ...prev]);
    setSelectedId(created._id);
    setCreatingNew(false);
    if (typeof onRecordChanged === "function") onRecordChanged();
    showSuccess("Telefon görüşmesi oluşturuldu.");
  };

  const handleSaved = (updated) => {
    if (!updated || !updated._id) return;
    setList((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
    showSuccess("Telefon görüşmesi güncellendi.");
    if (typeof onRecordChanged === "function") onRecordChanged();
  };

  const handleDelete = (rec) => {
    if (!rec?._id) return;
    setPendingDelete(rec);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?._id) return;
    setDeleteLoading(true);
    try {
      const promise = deletePhoneCall({
        token,
        patientId,
        phoneCallId: pendingDelete._id,
        ...(examinationId ? { examinationId } : {}),
      }).unwrap();
      const wrapped = registerAutoSave ? registerAutoSave(promise) : promise;
      await wrapped;
      setList((prev) => prev.filter((r) => r._id !== pendingDelete._id));
      if (selectedId === pendingDelete._id) setSelectedId(null);
      showSuccess("Telefon görüşmesi silindi.");
      if (typeof onRecordChanged === "function") onRecordChanged();
    } catch (_) {
      showError("Telefon görüşmesi silinemedi.");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const handleEditorError = useCallback(
    (err) => {
      const status = err?.status || err?.originalStatus;
      if (status === 423) {
        showError(
          err?.data?.message ||
            `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`
        );
        return;
      }
      showError("Telefon görüşmesi kaydedilemedi.");
    },
    [showError, terms]
  );

  const showLoading = mode === "standalone" && !initialLoaded && isLoadingList;

  // Oluşturma akışı sürerken buton gizlenir (Anamnez ile aynı)
  const headerAction =
    canEdit && !creatingNew ? (
      <ActionButton
        variant="add"
        compact
        palette={palette}
        onClick={handleNew}
        aria-label="Yeni telefon görüşmesi ekle"
        label="Yeni Görüşme"
      />
    ) : null;

  const showEditor = creatingNew || Boolean(selected);

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody
        title="Telefon Görüşmeleri"
        count={list.length}
        action={headerAction}
        alert={localAlert}
        onAlertClose={hideLocal}
      >
        {showLoading ? (
          <WorkspaceSkeleton />
        ) : list.length === 0 && !creatingNew ? (
          <WorkspaceEmpty
            icon={<PhoneIcon />}
            title="Henüz telefon görüşmesi yok"
            lead={`${terms.patientIns} yapılan ilk görüşmeyi`}
            linkLabel="kaydedin"
            tail="Konu, arayan kişi ve geri dönüş gereksinimi tek kayıtta tutulur."
            readOnlyText={`Bu ${terms.patientLower} için kaydedilmiş telefon görüşmesi bulunmuyor.`}
            onCreate={canEdit ? handleNew : undefined}
            palette={palette}
          />
        ) : (
          <WorkspaceSplit
            list={
              <Stack divider={<Box sx={{ borderBottom: `1px solid ${COLORS.slate100}` }} />}>
                {creatingNew && (
                  <CreatingRow
                    label="Yeni görüşme oluşturuluyor…"
                    hint="Konu yazıp Kaydet'e basın"
                    onCancel={handleCancelNew}
                    palette={palette}
                  />
                )}
                {list.map((item) => (
                  <SelectableListRow
                    key={item._id}
                    active={item._id === selectedId}
                    onClick={() => handleSelect(item._id)}
                    sx={LIST_ROW_SX}
                    title={item.subject || "Konu yok"}
                    subtitle={
                      <>
                        {(item.caller || item.callback) && (
                          <RowLine flex sx={{ mt: 0.25, fontSize: "0.8rem" }}>
                            {item.caller ? `Arayan: ${item.caller}` : null}
                            {item.callback && (
                              <RowChip
                                label="Geri dönüş"
                                sx={{
                                  bgcolor: alpha(COLORS.warning, 0.12),
                                  color: COLORS.warningDarker,
                                }}
                              />
                            )}
                          </RowLine>
                        )}
                        <RowEditorLine
                          editor={formatEditor(item.updatedBy || item.createdBy)}
                          rel={formatRelativeTr(item.updatedAt || item.createdAt)}
                        />
                      </>
                    }
                    trailing={<RowMetaText>{formatCallDate(item.date)}</RowMetaText>}
                  />
                ))}
              </Stack>
            }
            editor={
              !showEditor ? (
                <SelectPrompt
                  icon={<PhoneIcon />}
                  title="Bir görüşme seçin"
                  description="Detayları görmek ve düzenlemek için soldaki listeden bir görüşme seçin."
                />
              ) : (
                <PhoneCallEditor
                  key={selected?._id || "new"}
                  record={selected}
                  mode={mode}
                  patientId={patientId}
                  token={token}
                  examinationId={examinationId}
                  canEdit={canEdit}
                  onCreated={handleCreated}
                  onSaved={handleSaved}
                  onDelete={handleDelete}
                  registerAutoSave={registerAutoSave}
                  registerFlush={registerFlush}
                  onSaveStatusChange={onSaveStatusChange}
                  onError={handleEditorError}
                />
              )
            }
          />
        )}
      </WorkspaceBody>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Telefon görüşmesini sil"
        message={
          pendingDelete?.subject
            ? `"${pendingDelete.subject}" konulu görüşme kalıcı olarak silinecek. Devam edilsin mi?`
            : "Bu telefon görüşmesi kalıcı olarak silinecek. Devam edilsin mi?"
        }
        confirmText="Sil"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={deleteLoading}
      />
    </Box>
  );
}
